import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";
import { captureUtm } from "./lib/utm";
import { getPracticeVisibilityOwnerSecret } from "./lib/practiceVisibilitySession";
import { getPracticeVisibilityVisitorId } from "./lib/practiceVisibilityVisitor";

// Capture UTM params from URL on first page load (first-touch attribution)
captureUtm();

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
 if (!(error instanceof TRPCClientError)) return;
 if (typeof window === "undefined") return;

 const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

 if (!isUnauthorized) return;

 window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
 if (event.type === "updated" && event.action.type === "error") {
 const error = event.query.state.error;
 redirectToLoginIfUnauthorized(error);
 console.error("[API Query Error]", error);
 }
});

queryClient.getMutationCache().subscribe(event => {
 if (event.type === "updated" && event.action.type === "error") {
 const error = event.mutation.state.error;
 redirectToLoginIfUnauthorized(error);
 console.error("[API Mutation Error]", error);
 }
});

const trpcClient = trpc.createClient({
 links: [
 httpBatchLink({
 url: "/api/trpc",
	 transformer: superjson,
		 fetch(input, init) {
		 const headers = new Headers(input instanceof Request ? input.headers : undefined);
		 new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
		 const visitorId = getPracticeVisibilityVisitorId();
		 if (visitorId) headers.set("x-practice-visibility-visitor", visitorId);
		 const scanMatch = window.location.pathname.match(/^\/practice-visibility\/scan\/([^/]+)$/);
	 if (scanMatch?.[1]) {
	 const ownerSecret = getPracticeVisibilityOwnerSecret(scanMatch[1]);
	 if (ownerSecret) headers.set("x-practice-visibility-owner", ownerSecret);
	 }
	 return globalThis.fetch(input, {
	 ...(init ?? {}),
	 headers,
	 credentials: "include",
	 });
 },
 }),
 ],
});

createRoot(document.getElementById("root")!).render(
 <trpc.Provider client={trpcClient} queryClient={queryClient}>
 <QueryClientProvider client={queryClient}>
 <App />
 </QueryClientProvider>
 </trpc.Provider>
);
